import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, ArrowRight, Zap } from 'lucide-react';

const ComparisonSalesforce = () => {
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
    window.location.href = homeUrl;
  };

  return (
    <Layout>
      <SEOHead 
        title="NoQ5 vs Salesforce Agentforce vs Einstein: Best AI Copilot for Sales Reps 2025"
        description="NoQ5 beats Agentforce & Einstein for rep productivity: €59/user/mo, voice-first, GDPR-compliant. 7-day free trial. No complex setup."
        keywords="Salesforce Einstein vs NoQ5, Agentforce comparison, Salesforce AI copilot alternative, GDPR compliant Salesforce AI, NoQ5 vs Agentforce, Gong alternative, Clari alternative"
        canonicalUrl="https://noq5.ai/comparison/salesforce-einstein"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "NoQ5.ai vs Salesforce Einstein/Agentforce – Complete Comparison",
          "description": "In-depth comparison of NoQ5.ai and Salesforce Einstein/Agentforce for sales teams",
          "author": { "@type": "Organization", "name": "NoQ5.ai" },
          "publisher": { "@type": "Organization", "name": "NoQ5.ai" },
          "datePublished": "2025-10-09"
        }]}
      />
      <div className="container px-4 md:px-6 py-12 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              <span className="inline-block rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                Head-to-Head Comparison
              </span>
              <span className="inline-block rounded-full bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-600">
                ⏳ Salesforce Coming Soon
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
              NoQ5.ai vs Salesforce Einstein/Agentforce
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Both are powerful Salesforce AI solutions, but they serve different needs. Here's an honest comparison to help you choose.
            </p>
            <p className="text-lg text-primary font-medium mt-4">
              ✅ HubSpot integration is live now! Salesforce integration coming very soon.
            </p>
          </div>

          {/* Quick Verdict */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-primary/5 via-card to-primary/5 border-2 border-primary/20 rounded-2xl p-8 mb-12"
          >
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-foreground">
              <Zap className="w-6 h-6 text-primary" /> Quick Verdict
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              <strong className="text-foreground">Choose Einstein/Agentforce if:</strong> You're a large enterprise with dedicated Salesforce admins, need heavy analytics/governance, and have budget for extensive configuration.
            </p>
            <p className="text-lg text-muted-foreground">
              <strong className="text-foreground">Choose NoQ5.ai if:</strong> You're a sales team using Salesforce or HubSpot that needs instant CRM updates with quick setup, GDPR compliance built-in, and voice-first interface that reps actually want to use.
            </p>
          </motion.div>

          {/* Feature Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-2xl shadow-lg overflow-hidden border border-border mb-12"
          >
            <div className="bg-primary p-6">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-primary-foreground">Feature-by-Feature Comparison</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left py-4 px-6 font-semibold text-foreground">Feature</th>
                    <th className="text-center py-4 px-6 font-semibold text-foreground">Einstein/Agentforce</th>
                    <th className="text-center py-4 px-6 font-semibold text-primary">NoQ5.ai</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Setup Time', einstein: { good: false, text: 'Weeks (admin required)' }, noq5: { good: true, text: '5 minutes (quick OAuth)' } },
                    { feature: 'Voice Interface Quality', einstein: { good: false, text: 'Basic, limited', warning: true }, noq5: { good: true, text: 'Full conversational AI' } },
                    { feature: 'Mobile Experience', einstein: { good: false, text: 'Desktop-focused', warning: true }, noq5: { good: true, text: 'Mobile-first design' } },
                    { feature: 'EU Data Residency', einstein: { good: false, text: 'Requires configuration', warning: true }, noq5: { good: true, text: 'Built-in (Ireland)' } },
                    { feature: 'GDPR Anonymization', einstein: { good: false, text: 'Manual setup needed' }, noq5: { good: true, text: 'Automatic' } },
                    { feature: 'Analytics & Insights', einstein: { good: true, text: 'Deep, enterprise-grade' }, noq5: { good: false, text: 'Basic (focused on reps)', warning: true } },
                    { feature: 'Workflow Automation', einstein: { good: true, text: 'Extensive' }, noq5: { good: false, text: 'Focused on CRM updates', warning: true } },
                    { feature: 'SMB Pricing', einstein: { good: false, text: 'Enterprise-focused' }, noq5: { good: true, text: 'SMB-friendly' } },
                    { feature: 'Rep Adoption Rate', einstein: { good: false, text: 'Moderate (training needed)', warning: true }, noq5: { good: true, text: 'High (instant)' } },
                  ].map((row, i) => (
                    <tr key={i} className={`border-b border-border ${i % 2 === 1 ? 'bg-muted/30' : ''}`}>
                      <td className="py-4 px-6 font-medium text-foreground">{row.feature}</td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center">
                          {row.einstein.good ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mb-1" />
                          ) : row.einstein.warning ? (
                            <XCircle className="w-5 h-5 text-amber-500 mb-1" />
                          ) : (
                            <XCircle className="w-5 h-5 text-destructive mb-1" />
                          )}
                          <span className={`text-sm ${row.einstein.good ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                            {row.einstein.text}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center">
                          {row.noq5.good ? (
                            <CheckCircle className="w-5 h-5 text-primary mb-1" />
                          ) : row.noq5.warning ? (
                            <XCircle className="w-5 h-5 text-amber-500 mb-1" />
                          ) : (
                            <XCircle className="w-5 h-5 text-destructive mb-1" />
                          )}
                          <span className={`text-sm ${row.noq5.good ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
                            {row.noq5.text}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Detailed Breakdown */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-muted/50 rounded-2xl p-8 border border-border"
            >
              <h3 className="text-2xl font-bold mb-6 text-foreground">Salesforce Einstein/Agentforce Strengths</h3>
              <ul className="space-y-4">
                {[
                  { title: 'Enterprise Analytics', desc: 'Deep insights, forecasting, and business intelligence tools' },
                  { title: 'Native Platform', desc: 'Deeply integrated with all Salesforce features and clouds' },
                  { title: 'Workflow Automation', desc: 'Complex, multi-step automation capabilities' },
                  { title: 'Governance', desc: 'Enterprise-grade security, audit trails, and compliance features' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-foreground"><strong>{item.title}:</strong> {item.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-primary/5 to-secondary rounded-2xl p-8 border-2 border-primary/20"
            >
              <h3 className="text-2xl font-bold mb-6 text-primary">NoQ5.ai Strengths</h3>
              <ul className="space-y-4">
                {[
                  { title: 'Quick Setup', desc: 'OAuth token connection, no admin configuration required' },
                  { title: 'Voice-First', desc: 'Natural conversation that feels like talking to a human assistant' },
                  { title: 'GDPR Built-In', desc: 'EU data residency and automatic anonymization' },
                  { title: 'Rep-Focused', desc: "Built specifically for sales reps' daily workflow needs" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-foreground"><strong>{item.title}:</strong> {item.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-primary rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">Ready to try NoQ5.ai?</h3>
            <p className="text-lg mb-8 text-primary-foreground/80 max-w-2xl mx-auto">
              Experience the AI copilot built for sales reps using Salesforce or HubSpot. Start updating your CRM with voice in under 5 minutes.
            </p>
            <Button 
              onClick={scrollToDemo}
              size="lg"
              className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-xl"
            >
              Try NoQ5.ai Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default ComparisonSalesforce;